/**
 * Complete-password-reset screen (T27.2.10.1 Phase B, E4/E10).
 *
 * The reset link in the email points at `/reset/:token`. This screen takes the
 * new password and calls `useResetPassword` (→ POST /api/auth/reset). On success
 * the backend sets the password, revokes the account's other sessions, and issues
 * a fresh one — applied here (shared `useApplyAuthSuccess`) so the user lands
 * signed in. On an invalid / expired token the backend 400s and this shows a
 * "link expired — request a new one" message linking back to the request screen.
 *
 * Web + native (native flag forwarded so the issued session returns the refresh
 * token in the body). Mirrors the LoginPage card layout.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useResetPassword } from '@shopista/shared/api/hooks';
import { apiErrorMessage, ApiError } from '@shopista/shared/api';
import { Button, Card, Input, Logo } from '../../components';
import { type WebTokenStorage } from '../../auth';
import { routes } from '../../routes/paths';
import { isNativePlatform } from '../../routes/nativeGoogleSignIn';
import { useApplyAuthSuccess } from './useApplyAuthSuccess';
import {
  authPageStyle,
  authStackStyle,
  authWordmarkRowStyle,
  authCardBodyStyle,
  authHeadingStyle,
  authSubheadingStyle,
  authFormStyle,
  authInlineNoteStyle,
  authInlineErrorStyle,
  authLinkStyle,
  MIN_PASSWORD_LENGTH,
} from './authLayout';

export interface ResetPageProps {
  tokens: WebTokenStorage;
}

export function ResetPage({ tokens }: ResetPageProps): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const reset = useResetPassword();
  const applyAuthSuccess = useApplyAuthSuccess(tokens);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const onSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();
      setFormError(null);
      if (!token) {
        setLinkError('This reset link is missing its token.');
        return;
      }
      if (password.length < MIN_PASSWORD_LENGTH) {
        setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
        return;
      }
      if (password !== confirm) {
        setFormError('Passwords do not match.');
        return;
      }
      const native = isNativePlatform();
      reset.mutate(
        native ? { token, password, native: true } : { token, password },
        {
          onSuccess: applyAuthSuccess,
          onError: (err) => {
            // A 400 here is an invalid/expired/used token — route to the
            // "request a new one" path, not an inline field error.
            if (err instanceof ApiError && err.status === 400) {
              setLinkError(
                apiErrorMessage(err, 'This reset link is invalid or has expired.'),
              );
              return;
            }
            setFormError(apiErrorMessage(err, 'Could not reset your password.'));
          },
        },
      );
    },
    [token, password, confirm, reset, applyAuthSuccess],
  );

  return (
    <main style={authPageStyle} data-testid="reset-page">
      <div style={authStackStyle}>
        <div style={authWordmarkRowStyle}>
          <Logo size={80} showText data-testid="reset-logo" />
        </div>
        <Card padding="lg" shadow="md" data-testid="reset-card">
          <div style={authCardBodyStyle}>
            {linkError ? (
              <>
                <h1 style={authHeadingStyle}>Link expired</h1>
                <p role="alert" style={authInlineErrorStyle} data-testid="reset-link-error">
                  {linkError}
                </p>
                <p style={authInlineNoteStyle}>
                  Reset links work for 1 hour and can be used once.{' '}
                  <Link to={routes.resetRequest} style={authLinkStyle} data-testid="reset-request-new">
                    Request a new one
                  </Link>
                  .
                </p>
              </>
            ) : (
              <>
                <h1 style={authHeadingStyle}>Set a new password</h1>
                <p style={authSubheadingStyle}>
                  Choose a new password. This signs you out everywhere else.
                </p>
                <form
                  style={authFormStyle}
                  onSubmit={onSubmit}
                  data-testid="reset-form"
                  noValidate
                >
                  <Input
                    label="New password"
                    type="password"
                    name="new-password"
                    autoComplete="new-password"
                    reveal
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    data-testid="reset-password"
                    required
                  />
                  <Input
                    label="Confirm password"
                    type="password"
                    name="confirm-password"
                    autoComplete="new-password"
                    reveal
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm"
                    required
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={reset.isPending}
                    data-testid="reset-submit"
                  >
                    {reset.isPending ? 'Setting password…' : 'Set new password'}
                  </Button>
                  {formError && (
                    <p role="alert" data-testid="reset-error" style={authInlineErrorStyle}>
                      {formError}
                    </p>
                  )}
                </form>
              </>
            )}
          </div>
        </Card>
      </div>
    </main>
  );
}
